import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Divider, useMediaQuery, useTheme } from '@mui/material'
import { useSearchQuery } from '@src/pages/[country]/[language]/search/[searchTerm]'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import countriesMap from '@localization/countries.json'
import NoResults from '@src/routes/NewSearchResults/NoResults'
import ProductSearchResultsSkeleton from '../../ProductSearchResultsSkeleton/ProductSearchResultsSkeleton'
import ProductSearchShoppingAiBanner from '../../ProductSearchShoppingAiBanner/ProductSearchShoppingAiBanner'
import Pagination from '../../Pagination'
import InjectPromoProducts from '../../InjectPromoProducts'
import Gene from '../../GeneSearchResults/Gene'
import { useSearchContext } from '../../SearchContext'
import SearchResultsMenu from '../../SearchResultsMenu/SearchResultsMenu'
import { ResponsiveProduct } from '../../ResponsiveProduct/ResponsiveProduct'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'

const useStyles = makeStyles<void, 'noBorder'>()((theme, _params, classes) => ({
  searchResultsLoose: {
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
    },
  },
  // used for accordion version - CX-2114/CX-2202
  noBorder: {
    border: 'none',
  },
  results: {
    '&> *': {
      [`&:has(+ .${classes.noBorder})`]: {
        border: 'none',
      },
    },
    [theme.breakpoints.up('md')]: {
      '&> *:first-of-type': {},
      '&> *:last-of-type': {
        marginBottom: 0,
      },
    },
  },
  divider: {
    borderColor: theme.palette.grey[400],
    margin: theme.spacing(0),
    [theme.breakpoints.up('lg')]: {
      marginLeft: theme.spacing(27.5),
    },
  },
}))

const NewProductSearchResult = (): JSX.Element => {
  const { classes } = useStyles()
  const theme = useTheme()

  const { productResults, hasResults, loading, searchTerm } = useSearchContext()
  const [query, , queryActions] = useSearchQuery()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isUsUser = useCheckUserCountry(countriesMap.US.countryCode)
  const showShoppingAiBanner = isDesktop && isUsUser

  const [isBuyBoxSheetOpen, setIsBuyBoxSheetOpen] = useState(false)
  const [mobileSelectedMaterial, setMobileSelectedMaterial] =
    useState<ValidMaterialPricingDetailFragment | null>(null)

  const handleCloseBuyBoxSheet = () => {
    setMobileSelectedMaterial(null)
    setIsBuyBoxSheetOpen(false)
  }

  // TODO - Type next line
  const renderSearchResultItem = (item: any, index: number) => {
    const isGene = item && item.variants

    return (
      <React.Fragment key={index}>
        {isGene && showShoppingAiBanner ? (
          <div className={classes.noBorder}>
            <ProductSearchShoppingAiBanner />
          </div>
        ) : isGene ? (
          <Gene key={index} gene={item} inProductSRP />
        ) : (
          <>
            <ResponsiveProduct
              product={item}
              index={index}
              isBuyBoxSheetOpen={isBuyBoxSheetOpen}
              setIsBuyBoxSheetOpen={setIsBuyBoxSheetOpen}
              mobileSelectedMaterial={mobileSelectedMaterial}
              setMobileSelectedMaterial={setMobileSelectedMaterial}
              handleCloseBuyBoxSheet={handleCloseBuyBoxSheet}
            />
            <Divider className={classes.divider} />
          </>
        )}
      </React.Fragment>
    )
  }

  const renderSearchResults = () => {
    if (loading) {
      return <ProductSearchResultsSkeleton />
    }

    const products = productResults?.products

    if (!products) {
      return null
    }

    const numPages = Math.ceil((productResults?.metadata?.count ?? 0) / 10)

    return (
      <>
        <SearchResultsMenu />
        <div
          className={classes.results}
          data-testid="new-product-search-results"
        >
          <InjectPromoProducts
            searchKey={searchTerm || ''}
            key={searchTerm || ''}
            variant="dark"
          >
            {products.map(renderSearchResultItem)}
          </InjectPromoProducts>
          <Pagination
            currentPage={query.page}
            numPages={numPages}
            handleUpdatePage={queryActions.handlePageChange}
          />
        </div>
      </>
    )
  }

  return (
    <div>
      {hasResults || loading ? (
        <div className={classes.searchResultsLoose}>
          {renderSearchResults()}
        </div>
      ) : (
        <NoResults />
      )}
    </div>
  )
}

export default NewProductSearchResult
